import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Cloud, Plus, Trash2, Edit2, RefreshCw, Check, X,
  AlertTriangle, Settings, Server, Globe, Rocket, Shield,
  Key, FileText, ExternalLink, ToggleLeft, ToggleRight,
  ShieldCheck, ShieldAlert, Loader2, HelpCircle, Clock, Search
} from 'lucide-react';
import { accountsAPI, tenantsAPI, providerIcons } from '../services/amidala';
import api from '../services/api';
import axios from 'axios';
import { toast } from 'sonner';
import DemoModeToggle from '../components/DemoModeToggle';

/**
 * PreflightBadge — surfaces whether the Beskar pod can actually assume the
 * configured AWS IAM role BEFORE the first scan runs. Reads the cached result
 * on mount and lets the user re-run the STS AssumeRole probe on demand.
 */
const PreflightBadge = ({ account, defaultOpen = false }) => {
  const [preflight, setPreflight] = useState(account.preflight || null);
  const [checking, setChecking] = useState(false);
  const [showWizard, setShowWizard] = useState(defaultOpen);
  // Diagnostic for the Beskar pod's own AWS identity (not the customer's role).
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);

  // If parent flips defaultOpen after mount (e.g. accounts loaded via async
  // fetch and then URL param matched), open the wizard once.
  useEffect(() => {
    if (defaultOpen) setShowWizard(true);
  }, [defaultOpen]);

  useEffect(() => {
    // If we don't already have cached data on the account payload, fetch it
    if (!preflight) {
      api.get(`/amidala/accounts/${account.id}/preflight`)
        .then((r) => setPreflight(r.data))
        .catch(() => {});
    }
  }, [account.id, preflight]);

  const runPreflight = async () => {
    try {
      setChecking(true);
      const r = await api.post(`/amidala/accounts/${account.id}/preflight`);
      setPreflight(r.data);
      if (r.data.status === 'pass') {
        toast.success('Preflight passed — real scans will run');
      } else if (r.data.status === 'fail') {
        toast.error('Preflight failed — see details on the badge');
      }
    } catch (err) {
      toast.error('Preflight probe failed to run');
    } finally {
      setChecking(false);
    }
  };

  const runDiagnostic = async () => {
    try {
      setDiagnosing(true);
      const r = await api.get(`/amidala/preflight/beskar-identity`);
      setDiagnosis(r.data);
      const v = r.data?.verdict;
      if (v === 'pass') toast.success('Beskar pod is authenticated to AWS');
      else if (v === 'no_credentials') toast.error('Beskar pod has NO AWS credentials — redeploy needed');
      else if (v === 'invalid_credentials') toast.error('Beskar pod keys were rejected by AWS');
      else toast.error(`Diagnostic verdict: ${v || 'unknown'}`);
    } catch (err) {
      toast.error('Diagnostic call failed');
    } finally {
      setDiagnosing(false);
    }
  };

  const status = preflight?.status || 'unknown';
  const styles = {
    pass:    'bg-emerald-50 border-emerald-200 text-emerald-800',
    fail:    'bg-red-50 border-red-200 text-red-800',
    unknown: 'bg-amber-50 border-amber-200 text-amber-800',
    'n/a':   'bg-gray-50 border-gray-200 text-gray-600',
  };
  const icons = {
    pass:    <ShieldCheck className="w-4 h-4" />,
    fail:    <ShieldAlert className="w-4 h-4" />,
    unknown: <HelpCircle className="w-4 h-4" />,
    'n/a':   <Shield className="w-4 h-4" />,
  };
  const labels = {
    pass: 'Preflight passed',
    fail: 'Preflight failed',
    unknown: 'Preflight not run',
    'n/a': 'Preflight N/A',
  };

  return (
    <div className={`border rounded-lg p-3 mb-3 ${styles[status]}`}
         data-testid={`preflight-badge-${account.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          {icons[status]}
          <span>{labels[status]}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowWizard(true)}
            className="text-xs font-medium px-2 py-1 rounded border border-current opacity-80 hover:opacity-100"
            data-testid={`preflight-wizard-btn-${account.id}`}
            title="Open the setup wizard"
          >
            Setup Guide
          </button>
          <button
            onClick={runPreflight}
            disabled={checking}
            className="text-xs font-medium px-2 py-1 rounded border border-current opacity-80 hover:opacity-100 flex items-center gap-1"
            data-testid={`preflight-run-btn-${account.id}`}
          >
            {checking ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {preflight ? 'Recheck' : 'Run Preflight'}
          </button>
        </div>
      </div>
      {preflight?.message && (
        <div className="text-xs mt-2 opacity-90 leading-relaxed">
          {preflight.message.slice(0, 240)}
          {preflight.message.length > 240 && '…'}
        </div>
      )}
      {preflight?.checked_at && (
        <div className="text-[10px] opacity-60 mt-1">
          Checked {new Date(preflight.checked_at).toLocaleString()}
        </div>
      )}

      {/* Beskar pod-identity diagnostic — surfaces WHY preflight is failing
          (missing env vars, invalid keys, etc.) so admins don't have to guess. */}
      {(preflight?.reason === 'beskar_pod_missing_credentials'
        || preflight?.status === 'fail') && (
        <div className="mt-3 pt-3 border-t border-current/20">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] font-semibold opacity-80">
              Beskar pod AWS identity
            </div>
            <button
              onClick={runDiagnostic}
              disabled={diagnosing}
              className="text-xs font-medium px-2 py-1 rounded border border-current opacity-80 hover:opacity-100 flex items-center gap-1"
              data-testid={`beskar-diagnose-btn-${account.id}`}
            >
              {diagnosing
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Search className="w-3 h-3" />}
              {diagnosis ? 'Re-diagnose' : 'Diagnose Beskar Pod'}
            </button>
          </div>
          {diagnosis && (
            <div
              className={`mt-2 rounded border p-2 text-xs ${
                diagnosis.verdict === 'pass'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-red-50 border-red-200 text-red-900'
              }`}
              data-testid={`beskar-diagnose-result-${account.id}`}
            >
              <div className="font-semibold mb-1">
                Verdict: {diagnosis.verdict}
              </div>
              <div className="opacity-90 mb-2">{diagnosis.message}</div>
              {diagnosis.identity && (
                <div className="font-mono text-[10px] break-all opacity-90 mb-1">
                  {diagnosis.identity.Arn}
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono opacity-80">
                {Object.entries(diagnosis.env || {}).map(([k, v]) => (
                  <div key={k} className="truncate">
                    <span className="opacity-60">{k}:</span>{' '}
                    <span>{v === null ? '—' : String(v)}</span>
                  </div>
                ))}
              </div>
              {Array.isArray(diagnosis.next_steps) && diagnosis.next_steps.length > 0 && (
                <ul className="mt-2 list-disc list-inside text-[11px] opacity-90 space-y-0.5">
                  {diagnosis.next_steps.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {showWizard && (
        <PreflightSetupWizard
          account={account}
          onClose={() => setShowWizard(false)}
          onPreflightPassed={(pf) => { setPreflight(pf); setShowWizard(false); }}
        />
      )}
    </div>
  );
};

/**
 * PreflightSetupWizard — modal that walks the admin through:
 *  1. Confirming Beskar has AWS creds (or contact Emergent Support).
 *  2. Copy-paste-ready trust policy JSON for their target role.
 *  3. Required managed policies (SecurityAudit + ViewOnlyAccess).
 *  4. One-click "Run Preflight" to verify.
 *
 * Backed by GET /amidala/accounts/{id}/preflight/setup-guide.
 */
const PreflightSetupWizard = ({ account, onClose, onPreflightPassed }) => {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [roleArnDraft, setRoleArnDraft] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    api.get(`/amidala/accounts/${account.id}/preflight/setup-guide`)
      .then((r) => {
        setGuide(r.data);
        setRoleArnDraft(r.data.role_arn || '');
      })
      .catch(() => toast.error('Failed to load setup guide'))
      .finally(() => setLoading(false));
  }, [account.id]);

  const copyToClipboard = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2500);
    } catch { toast.error('Copy failed — select the text and copy manually'); }
  };

  const saveRoleArn = async () => {
    if (!roleArnDraft.startsWith('arn:aws:iam::') || !roleArnDraft.includes(':role/')) {
      toast.error('Enter a valid role ARN (arn:aws:iam::123456789012:role/Name)');
      return;
    }
    try {
      setSavingRole(true);
      await api.put(`/amidala/accounts/${account.id}/role-arn`, {
        role_arn: roleArnDraft.trim(),
        external_id: guide?.external_id || '',
      });
      toast.success('Role ARN saved');
      setGuide((g) => g ? { ...g, role_arn: roleArnDraft.trim() } : g);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save role ARN');
    } finally { setSavingRole(false); }
  };

  const testPreflight = async () => {
    try {
      setTesting(true);
      const r = await api.post(`/amidala/accounts/${account.id}/preflight`);
      if (r.data.status === 'pass') {
        toast.success('Preflight passed! Beskar can now scan this account.');
        onPreflightPassed && onPreflightPassed(r.data);
      } else {
        toast.error(`Still failing — ${r.data.reason || 'see message'}`);
      }
    } catch { toast.error('Preflight call failed'); }
    finally { setTesting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
         data-testid="preflight-wizard-modal">
      <div className="bg-white text-slate-900 rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-xl">
        <div className="flex items-start justify-between p-5 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-semibold">AWS Configuration Steps</h2>
            <p className="text-xs text-slate-500 mt-1">
              For account <span className="font-mono">{account.name}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none"
                  data-testid="preflight-wizard-close">×</button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : !guide ? (
          <div className="p-8 text-center text-red-600 text-sm">Failed to load setup guide.</div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Beskar Account ID banner — no env vars required */}
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4"
                 data-testid="wizard-beskar-account-id-card">
              <div className="text-[10px] uppercase tracking-wide text-indigo-800/70 font-semibold">
                Beskar AWS Account ID
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-2xl font-mono font-bold text-indigo-900 tracking-wider"
                      data-testid="wizard-beskar-account-id">
                  {guide.beskar_account_id_pretty}
                </span>
                <button
                  onClick={() => copyToClipboard(guide.beskar_account_id, 'account_id')}
                  className="text-[11px] px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                  data-testid="wizard-copy-beskar-account-id"
                >
                  {copiedKey === 'account_id' ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-indigo-800/80 mt-1">
                Use this Account ID when creating cross-account IAM roles in AWS.
              </p>
            </div>

            {/* Steps */}
            {guide.steps.map((s) => (
              <div key={s.n} className={`rounded-lg border p-3 ${s.blocker ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-slate-50'}`}
                   data-testid={`preflight-step-${s.n}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${s.blocker ? 'bg-red-600 text-white' : 'bg-slate-700 text-white'}`}>
                    {s.n}
                  </span>
                  <div className="font-medium text-sm">{s.title}</div>
                  {s.blocker && (
                    <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white">BLOCKER</span>
                  )}
                </div>
                <p className="text-xs text-slate-600 mt-2 leading-relaxed">{s.why}</p>
                <p className="text-xs text-slate-800 mt-2 font-medium whitespace-pre-line">{s.action}</p>
                {s.policies && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.policies.map((p) => (
                      <span key={p} className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-800 text-white">{p}</span>
                    ))}
                  </div>
                )}
                {s.external_id && (
                  <div className="mt-2 flex items-center gap-2 bg-white border border-slate-300 rounded px-2 py-1.5">
                    <code className="font-mono text-xs text-slate-800 flex-1 truncate" data-testid={`wizard-external-id-${s.n}`}>
                      {s.external_id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(s.external_id, `ext_${s.n}`)}
                      className="text-[11px] px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                      data-testid={`wizard-copy-external-id-${s.n}`}
                    >
                      {copiedKey === `ext_${s.n}` ? '✓' : 'Copy'}
                    </button>
                  </div>
                )}
                {s.inline_policy_json && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wide text-slate-500">Inline policy JSON</span>
                      <button
                        onClick={() => copyToClipboard(s.inline_policy_json, `inline_${s.n}`)}
                        className="text-[11px] px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                        data-testid={`wizard-copy-inline-policy-${s.n}`}
                      >
                        {copiedKey === `inline_${s.n}` ? '✓ Copied' : 'Copy'}
                      </button>
                    </div>
                    <pre className="bg-slate-900 text-slate-100 text-[11px] p-2 rounded overflow-x-auto font-mono leading-relaxed max-h-56">
{s.inline_policy_json}
                    </pre>
                  </div>
                )}
                {s.trust_policy_json && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wide text-slate-500">Trust policy JSON</span>
                      <button
                        onClick={() => copyToClipboard(s.trust_policy_json, `trust_${s.n}`)}
                        className="text-[11px] px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                        data-testid={`wizard-copy-trust-policy-${s.n}`}
                      >
                        {copiedKey === `trust_${s.n}` ? '✓ Copied' : 'Copy'}
                      </button>
                    </div>
                    <pre className="bg-slate-900 text-slate-100 text-[11px] p-2 rounded overflow-x-auto font-mono leading-relaxed max-h-56">
{s.trust_policy_json}
                    </pre>
                  </div>
                )}
                {s.input_role_arn && (
                  <div className="mt-2">
                    <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-1">
                      Paste your Role ARN
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={roleArnDraft}
                        onChange={(e) => setRoleArnDraft(e.target.value)}
                        placeholder="arn:aws:iam::123456789012:role/BeskarAmidalaCSPM"
                        className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm font-mono"
                        data-testid="wizard-role-arn-input"
                      />
                      <button
                        onClick={saveRoleArn}
                        disabled={savingRole || !roleArnDraft}
                        className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        data-testid="wizard-save-role-arn"
                      >
                        {savingRole ? 'Saving…' : 'Save Role ARN'}
                      </button>
                    </div>
                    {s.current_role_arn && (
                      <div className="text-[10px] text-slate-500 mt-1 font-mono truncate">
                        Currently saved: {s.current_role_arn}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Test button */}
            <div className="border-t pt-4 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Once every blocker step is done, click below to verify the trust chain end-to-end.
              </p>
              <button
                onClick={testPreflight}
                disabled={testing}
                className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                data-testid="preflight-wizard-test-btn"
              >
                {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                {testing ? 'Testing…' : 'Test Preflight'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ScheduleSelector — per-account auto-scan cadence dropdown.
 * Reads /amidala/accounts/{id}/schedule on mount and PUTs on change.
 * Default cadence is 12h (twice a day) per Feb 2026 product decision.
 */
const CADENCE_OPTIONS = [
  { value: '1h',  label: 'Every 1 hour' },
  { value: '6h',  label: 'Every 6 hours' },
  { value: '12h', label: 'Every 12 hours (twice a day)' },
  { value: '24h', label: 'Once a day' },
  { value: '3d',  label: 'Every 3 days' },
  { value: '1w',  label: 'Every week' },
];

const ScheduleSelector = ({ account }) => {
  const [cadence, setCadence] = useState('12h');
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/amidala/accounts/${account.id}/schedule`)
      .then((r) => {
        setCadence(r.data.cadence || '12h');
        setEnabled(Boolean(r.data.enabled));
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [account.id]);

  const save = async (nextCadence, nextEnabled) => {
    try {
      setSaving(true);
      await api.put(`/amidala/accounts/${account.id}/schedule`, {
        cadence: nextCadence,
        enabled: nextEnabled,
      });
      setCadence(nextCadence);
      setEnabled(nextEnabled);
      toast.success(nextEnabled
        ? `Auto-scan set to ${CADENCE_OPTIONS.find(o => o.value === nextCadence)?.label || nextCadence}`
        : 'Auto-scan disabled for this account');
    } catch (err) {
      toast.error('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg p-3 mb-3 bg-indigo-50/40 border-indigo-100"
         data-testid={`schedule-selector-${account.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
          <Clock className="w-4 h-4" />
          <span>Auto-scan schedule</span>
        </div>
        <button
          type="button"
          onClick={() => save(cadence, !enabled)}
          disabled={saving || !loaded}
          className={`text-xs font-medium px-2 py-1 rounded border flex items-center gap-1 ${
            enabled
              ? 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
          data-testid={`schedule-toggle-${account.id}`}
        >
          {saving && <Loader2 className="w-3 h-3 animate-spin" />}
          {enabled ? 'Enabled' : 'Enable'}
        </button>
      </div>
      <select
        value={cadence}
        disabled={saving || !loaded}
        onChange={(e) => save(e.target.value, enabled)}
        className="w-full text-sm border rounded-md px-2 py-1.5 bg-white border-indigo-200 focus:ring-2 focus:ring-indigo-300 outline-none disabled:opacity-60"
        data-testid={`schedule-cadence-${account.id}`}
      >
        {CADENCE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <p className="text-[10px] text-indigo-800/70 mt-1">
        Default is twice a day (12h). Range: 1 hour → 1 week.
      </p>
    </div>
  );
};

/**
 * OperatorCredentialsPanel — self-serve store for the Beskar pod's OWN
 * AWS IAM user access key. Once saved, cross-account STS AssumeRole calls
 * (i.e. every AWS scan) use these creds instead of the pod's env vars,
 * permanently curing ``beskar_pod_missing_credentials`` failures in prod.
 *
 * Rendered at the top of the Accounts page for super_admin users only.
 * Secrets are encrypted (Fernet) at rest; GET returns masked data only.
 */
const OperatorCredentialsPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ access_key_id: '', secret_access_key: '', default_region: 'ap-south-1' });
  const [showSecret, setShowSecret] = useState(false);

  const currentUser = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch (_e) { return null; }
  }, []);
  const isSuperAdmin = currentUser?.is_super_admin || currentUser?.role === 'admin';

  const refresh = React.useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/amidala/operator-credentials');
      setStatus(r.data);
    } catch (_e) {
      // 403 for non-admin — hide panel entirely
      setStatus(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isSuperAdmin) refresh(); else setLoading(false); }, [isSuperAdmin, refresh]);

  if (!isSuperAdmin) return null;
  if (loading) return null;

  const configured = status?.db_configured || status?.env_configured;
  const source = status?.resolved_source;

  const save = async () => {
    if (!form.access_key_id.trim() || !form.secret_access_key.trim()) {
      toast.error('Access Key ID and Secret Access Key are both required');
      return;
    }
    try {
      setSaving(true);
      await api.put('/amidala/operator-credentials', form);
      toast.success('Operator credentials saved (encrypted). Click Test to verify with AWS.');
      setForm({ access_key_id: '', secret_access_key: '', default_region: form.default_region });
      setExpanded(false);
      await refresh();
      setTestResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credentials');
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    try {
      setTesting(true);
      const r = await api.post('/amidala/operator-credentials/test');
      setTestResult(r.data);
      if (r.data.verdict === 'pass') toast.success(`AWS STS authenticated (${r.data.identity?.Arn})`);
      else toast.error(r.data.message || 'AWS rejected the credentials');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test call failed');
    } finally { setTesting(false); }
  };

  const clear = async () => {
    if (!window.confirm('Delete the stored Beskar operator credentials? Scans will fall back to pod env vars.')) return;
    try {
      await api.delete('/amidala/operator-credentials');
      toast.success('Operator credentials cleared');
      setTestResult(null);
      await refresh();
    } catch (err) {
      toast.error('Failed to clear credentials');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-amber-200 overflow-hidden"
         data-testid="operator-credentials-panel">
      <div className="px-5 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            <Key className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Beskar Operator AWS Credentials</h3>
            <p className="text-xs text-gray-600 mt-0.5">
              Used by every AWS scan to <code className="text-[11px] bg-white px-1 py-0.5 rounded">sts:AssumeRole</code> into your accounts.
              Configure here to avoid <code className="text-[11px] bg-white px-1 py-0.5 rounded">beskar_pod_missing_credentials</code>.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-sm px-3 py-1.5 rounded-md border border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
          data-testid="operator-creds-toggle"
        >
          {expanded ? 'Hide' : (configured ? 'Manage' : 'Configure')}
        </button>
      </div>

      <div className="px-5 py-3 flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Status:</span>
          {configured ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5" /> Configured ({source})
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
              <ShieldAlert className="w-3.5 h-3.5" /> Not configured
            </span>
          )}
        </div>
        {status?.db_configured && (
          <div className="text-xs text-gray-600">
            Access Key: <span className="font-mono">{status.db_access_key_masked}</span>
            {status.db_default_region && <> · Region: <span className="font-mono">{status.db_default_region}</span></>}
          </div>
        )}
        {status?.env_configured && !status?.db_configured && (
          <div className="text-xs text-gray-600">
            Env key: <span className="font-mono">{status.env_access_key_masked}</span>
          </div>
        )}
        {configured && (
          <button
            type="button"
            onClick={runTest}
            disabled={testing}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-60"
            data-testid="operator-creds-test-btn"
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            Test with STS
          </button>
        )}
      </div>

      {testResult && (
        <div className={`mx-5 mb-3 p-3 rounded-lg border text-xs ${
          testResult.verdict === 'pass' ? 'bg-green-50 border-green-200 text-green-900' :
          'bg-red-50 border-red-200 text-red-900'
        }`} data-testid="operator-creds-test-result">
          {testResult.verdict === 'pass' ? (
            <div>
              <div className="font-medium">STS authenticated (source: {testResult.source})</div>
              <div className="mt-1 font-mono text-[11px]">
                Account {testResult.identity?.Account} · {testResult.identity?.Arn}
              </div>
            </div>
          ) : (
            <div>
              <div className="font-medium">Verdict: {testResult.verdict}</div>
              <div className="mt-1">{testResult.message}</div>
            </div>
          )}
        </div>
      )}

      {expanded && (
        <div className="px-5 pb-5 pt-2 border-t border-gray-100 bg-gray-50" data-testid="operator-creds-form">
          <p className="text-xs text-gray-600 mb-3">
            Paste the credentials of an IAM user in <strong>Beskar&apos;s own AWS account</strong> whose only permission is
            <code className="mx-1 bg-white px-1 py-0.5 rounded">sts:AssumeRole</code> on the roles you configured on customer accounts.
            The secret is encrypted at rest and never leaves this backend.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <label className="text-xs font-medium text-gray-700">AWS Access Key ID</label>
              <input
                type="text"
                value={form.access_key_id}
                onChange={(e) => setForm({ ...form, access_key_id: e.target.value.trim() })}
                placeholder="AKIA... (20 chars)"
                className="mt-1 w-full text-sm border rounded-md px-2 py-1.5 border-gray-300 focus:ring-2 focus:ring-indigo-300 outline-none font-mono"
                data-testid="operator-creds-access-key-input"
              />
            </div>
            <div className="md:col-span-1">
              <label className="text-xs font-medium text-gray-700">AWS Secret Access Key</label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={form.secret_access_key}
                  onChange={(e) => setForm({ ...form, secret_access_key: e.target.value })}
                  placeholder="40+ character secret"
                  className="mt-1 w-full text-sm border rounded-md px-2 py-1.5 pr-16 border-gray-300 focus:ring-2 focus:ring-indigo-300 outline-none font-mono"
                  data-testid="operator-creds-secret-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-indigo-600 hover:text-indigo-800"
                >
                  {showSecret ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div className="md:col-span-1">
              <label className="text-xs font-medium text-gray-700">Default Region</label>
              <input
                type="text"
                value={form.default_region}
                onChange={(e) => setForm({ ...form, default_region: e.target.value.trim() })}
                placeholder="ap-south-1"
                className="mt-1 w-full text-sm border rounded-md px-2 py-1.5 border-gray-300 focus:ring-2 focus:ring-indigo-300 outline-none font-mono"
                data-testid="operator-creds-region-input"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
              data-testid="operator-creds-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Credentials (Encrypted)
            </button>
            {status?.db_configured && (
              <button
                type="button"
                onClick={clear}
                className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md border border-red-300 text-red-700 bg-white hover:bg-red-50"
                data-testid="operator-creds-clear-btn"
              >
                <Trash2 className="w-4 h-4" /> Clear Stored Credentials
              </button>
            )}
            <a
              href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_credentials_access-keys.html"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1 ml-auto"
            >
              How to create an AWS access key <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * JiraTemplatesPanel — per-tenant severity → (project key, issue type)
 * mapping used by Send-to-Jira and Bulk Send-to-Jira. Falls back to the
 * global default_project_key + "Task" when a severity row is empty.
 */
const JiraTemplatesPanel = () => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [tenantId, setTenantId] = React.useState(null);
  const [tpl, setTpl] = React.useState({
    default_project_key: '',
    default_issue_type: 'Task',
    mappings: {
      critical: { project_key: '', issue_type: 'Bug' },
      high:     { project_key: '', issue_type: 'Bug' },
      medium:   { project_key: '', issue_type: 'Task' },
      low:      { project_key: '', issue_type: 'Task' },
      info:     { project_key: '', issue_type: 'Task' },
    },
  });

  const currentUser = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch (_e) { return null; }
  }, []);
  const isAdmin = currentUser?.is_super_admin || currentUser?.role === 'admin';

  const load = React.useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/amidala/tenants/${tenantId}/jira/templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTpl({
        default_project_key: r.data.default_project_key || '',
        default_issue_type: r.data.default_issue_type || 'Task',
        mappings: {
          critical: r.data.mappings?.critical || { project_key: '', issue_type: 'Bug' },
          high:     r.data.mappings?.high     || { project_key: '', issue_type: 'Bug' },
          medium:   r.data.mappings?.medium   || { project_key: '', issue_type: 'Task' },
          low:      r.data.mappings?.low      || { project_key: '', issue_type: 'Task' },
          info:     r.data.mappings?.info     || { project_key: '', issue_type: 'Task' },
        },
      });
    } catch (err) {
      // No template yet is fine — keep defaults.
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  React.useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/amidala/tenants`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (r.data?.[0]?.id) setTenantId(r.data[0].id);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const setMap = (sev, key, val) => {
    setTpl((prev) => ({
      ...prev,
      mappings: { ...prev.mappings, [sev]: { ...prev.mappings[sev], [key]: val } },
    }));
  };

  const save = async () => {
    if (!tenantId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/amidala/tenants/${tenantId}/jira/templates`,
        {
          default_project_key: tpl.default_project_key || null,
          default_issue_type: tpl.default_issue_type || 'Task',
          mappings: Object.fromEntries(Object.entries(tpl.mappings).map(
            ([sev, row]) => [sev, {
              project_key: row.project_key || null,
              issue_type: row.issue_type || 'Task',
            }]
          )),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Jira ticket templates saved.');
    } catch (err) {
      window.alert(`Save failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return null;

  const severityColors = {
    critical: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    high:     'bg-orange-500/20 text-orange-300 border-orange-500/30',
    medium:   'bg-amber-500/20 text-amber-300 border-amber-500/30',
    low:      'bg-blue-500/20 text-blue-300 border-blue-500/30',
    info:     'bg-slate-500/20 text-slate-300 border-slate-500/30',
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow p-5 mb-4 border border-slate-200 dark:border-slate-700"
         data-testid="jira-templates-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Rocket className="w-5 h-5 text-blue-500" />
            Jira Ticket Templates
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Choose which Jira project + issue type each severity should target when Send-to-Jira / Bulk Send-to-Jira fires.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-sm text-blue-600 hover:text-blue-500 font-medium"
          data-testid="jira-templates-toggle"
        >
          {expanded ? 'Hide' : 'Manage'}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3">
          {loading && <p className="text-xs text-slate-500">Loading…</p>}

          {!loading && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-slate-50 dark:bg-slate-900/40">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Default Project Key
                  </label>
                  <input
                    value={tpl.default_project_key}
                    onChange={(e) => setTpl((p) => ({ ...p, default_project_key: e.target.value.toUpperCase() }))}
                    placeholder="e.g. SEC"
                    className="w-full px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800 dark:text-white"
                    data-testid="jira-tpl-default-project"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Default Issue Type
                  </label>
                  <input
                    value={tpl.default_issue_type}
                    onChange={(e) => setTpl((p) => ({ ...p, default_issue_type: e.target.value }))}
                    placeholder="Task"
                    className="w-full px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800 dark:text-white"
                    data-testid="jira-tpl-default-issue-type"
                  />
                </div>
              </div>

              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-900/60">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Severity</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Project Key</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-600 dark:text-slate-300">Issue Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['critical', 'high', 'medium', 'low', 'info'].map((sev) => (
                      <tr key={sev} className="border-t border-slate-200 dark:border-slate-700">
                        <td className="px-3 py-2">
                          <span className={`inline-block text-[11px] uppercase font-semibold px-2 py-0.5 rounded border ${severityColors[sev]}`}>
                            {sev}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            value={tpl.mappings[sev]?.project_key || ''}
                            onChange={(e) => setMap(sev, 'project_key', e.target.value.toUpperCase())}
                            placeholder="(inherit default)"
                            className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800 dark:text-white"
                            data-testid={`jira-tpl-${sev}-project`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            value={tpl.mappings[sev]?.issue_type || ''}
                            onChange={(e) => setMap(sev, 'issue_type', e.target.value)}
                            placeholder="Task"
                            className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800 dark:text-white"
                            data-testid={`jira-tpl-${sev}-issue-type`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={load}
                  className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                  data-testid="jira-tpl-reset"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-60"
                  data-testid="jira-tpl-save"
                >
                  {saving ? 'Saving…' : 'Save templates'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const AmidalaAccounts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/amidala') ? '/amidala' : '/grc/amidala';
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  // Deep-link support: /accounts?openWizard=<account_id> auto-opens the
  // Preflight Setup Wizard for that account so a scan finding can jump the
  // user straight to the fix guide.
  const openWizardAccountId = (() => {
    try { return new URLSearchParams(location.search).get('openWizard') || ''; }
    catch { return ''; }
  })();
  // Clear the query param once we've handed it off, so refreshing the page
  // doesn't re-open the modal forever.
  const [consumedWizardParam, setConsumedWizardParam] = useState(false);
  useEffect(() => {
    if (openWizardAccountId && accounts.length > 0 && !consumedWizardParam) {
      setConsumedWizardParam(true);
      // strip the query so it doesn't stick around
      navigate(location.pathname, { replace: true });
    }
  }, [openWizardAccountId, accounts, consumedWizardParam, navigate, location.pathname]);
  
  const [newAccount, setNewAccount] = useState({
    name: '',
    provider: 'aws',
    credentials: {},
    regions: [],
    tags: {}
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  const handleTestConnection = async () => {
    const creds = newAccount.credentials;
    if (!creds.role_arn && !creds.client_id && !creds.project_id) {
      setConnectionStatus('error');
      return;
    }
    try {
      setTestingConnection(true);
      setConnectionStatus(null);
      const res = await accountsAPI.testConnection({
        provider: newAccount.provider,
        credentials: creds
      });
      setConnectionStatus(res.data?.success ? 'success' : 'error');
    } catch {
      setConnectionStatus('error');
    } finally {
      setTestingConnection(false);
    }
  };

  const providerRegions = {
    aws: ['us-east-1', 'us-east-2', 'us-west-1', 'us-west-2', 'eu-west-1', 'eu-west-2', 'eu-central-1', 'ap-southeast-1', 'ap-northeast-1'],
    azure: ['eastus', 'eastus2', 'westus', 'westus2', 'westeurope', 'northeurope', 'southeastasia', 'japaneast'],
    gcp: ['us-central1', 'us-east1', 'us-west1', 'europe-west1', 'europe-west2', 'asia-east1', 'asia-southeast1']
  };

  useEffect(() => {
    loadData();
  }, []);

  const toggleDemoMode = () => {
    // Legacy CTA — actual toggle is now in the header (DemoModeToggle component)
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Always load tenant (needed for adding accounts even with demo mode off)
      const tenantsRes = await tenantsAPI.list();
      if (tenantsRes.data.length > 0) {
        const currentTenant = tenantsRes.data[0];
        setTenant(currentTenant);
        
        // Backend applies the demo-mode filter automatically based on tenant settings
        try {
          const accountsRes = await accountsAPI.list(currentTenant.id);
          setAccounts(accountsRes.data);
        } catch {
          setAccounts([]);
        }
      }
    } catch (err) {
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async () => {
    if (!newAccount.name) {
      toast.error('Account name is required');
      return;
    }
    if (!tenant) {
      toast.error('No tenant found. Please set up your organization first.');
      return;
    }
    
    try {
      // Use user-entered credentials, falling back to defaults only if empty
      const userCreds = newAccount.credentials || {};
      const hasUserCreds = Object.values(userCreds).some(v => v && v.trim && v.trim() !== '');
      const credentials = hasUserCreds ? userCreds : getDefaultCredentials(newAccount.provider);

      const accountData = {
        ...newAccount,
        regions: newAccount.regions.length > 0 ? newAccount.regions : providerRegions[newAccount.provider].slice(0, 2),
        credentials,
      };
      
      await accountsAPI.create(tenant.id, accountData);
      toast.success('Account added successfully');
      setShowAddModal(false);
      setNewAccount({ name: '', provider: 'aws', credentials: {}, regions: [], tags: {} });
      setConnectionStatus(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add account');
    }
  };

  const getDefaultCredentials = (provider) => {
    switch (provider) {
      case 'aws':
        return { account_id: `${Math.floor(100000000000 + Math.random() * 900000000000)}` };
      case 'azure':
        return { subscription_id: `sub-${crypto.randomUUID().slice(0, 8)}`, tenant_id: `tenant-${crypto.randomUUID().slice(0, 8)}` };
      case 'gcp':
        return { project_id: `project-${crypto.randomUUID().slice(0, 8)}` };
      default:
        return {};
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this account? All associated findings will be deleted.')) {
      return;
    }
    
    try {
      await accountsAPI.delete(accountId);
      loadData();
    } catch (err) {
      setError('Failed to delete account');
    }
  };

  const toggleRegion = (region) => {
    setNewAccount(prev => ({
      ...prev,
      regions: prev.regions.includes(region)
        ? prev.regions.filter(r => r !== region)
        : [...prev.regions, region]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Cloud className="w-8 h-8 text-indigo-600" />
            Cloud Accounts
          </h1>
          <p className="text-gray-600 mt-1">Manage your connected cloud accounts</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Demo Mode Toggle (tenant-level, defaults OFF) */}
          {tenant && (
            <DemoModeToggle
              tenantId={tenant.id}
              onChange={() => loadData()}
            />
          )}

          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 h-10 px-4 bg-indigo-600 text-white text-sm font-medium rounded-full hover:bg-indigo-700 transition-colors"
            data-testid="add-account-btn"
          >
            <Plus className="w-4 h-4" />
            Add Account
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Beskar Operator Credentials — super_admin only, mounted above the
          account grid so setup happens BEFORE the first preflight/scan. */}
      <OperatorCredentialsPanel />

      {/* Jira Ticket Templates — admins map severity → (project key, issue type). */}
      <JiraTemplatesPanel />

      {/* Show onboarding when there are no accounts */}
      {accounts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Rocket className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Onboard Your First Cloud Environment</h2>
                <p className="text-indigo-100">Connect AWS, Azure, or GCP to start securing your cloud infrastructure</p>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="p-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Get Started in 3 Simple Steps</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Step 1 */}
              <div className="relative">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Choose Your Provider</h4>
                    <p className="text-sm text-gray-600">Select AWS, Azure, GCP, or On-Premises infrastructure</p>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Configure Access</h4>
                    <p className="text-sm text-gray-600">Set up read-only access with IAM roles or service accounts</p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Start Scanning</h4>
                    <p className="text-sm text-gray-600">Run your first security scan and get actionable insights</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Provider Cards */}
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Supported Cloud Providers</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="border-2 border-orange-200 bg-orange-50 rounded-xl p-6 text-center hover:border-orange-400 transition-all cursor-pointer" onClick={() => setShowAddModal(true)}>
                <span className="text-4xl mb-2 block">☁️</span>
                <h4 className="font-semibold text-gray-900">AWS</h4>
                <p className="text-xs text-gray-500 mt-1">Amazon Web Services</p>
              </div>
              <div className="border-2 border-blue-200 bg-blue-50 rounded-xl p-6 text-center hover:border-blue-400 transition-all cursor-pointer" onClick={() => setShowAddModal(true)}>
                <span className="text-4xl mb-2 block">☁️</span>
                <h4 className="font-semibold text-gray-900">Azure</h4>
                <p className="text-xs text-gray-500 mt-1">Microsoft Azure</p>
              </div>
              <div className="border-2 border-green-200 bg-green-50 rounded-xl p-6 text-center hover:border-green-400 transition-all cursor-pointer" onClick={() => setShowAddModal(true)}>
                <span className="text-4xl mb-2 block">☁️</span>
                <h4 className="font-semibold text-gray-900">GCP</h4>
                <p className="text-xs text-gray-500 mt-1">Google Cloud Platform</p>
              </div>
              <div className="border-2 border-purple-200 bg-purple-50 rounded-xl p-6 text-center hover:border-purple-400 transition-all cursor-pointer" onClick={() => setShowAddModal(true)}>
                <span className="text-4xl mb-2 block">🏢</span>
                <h4 className="font-semibold text-gray-900">On-Prem</h4>
                <p className="text-xs text-gray-500 mt-1">Self-hosted infrastructure</p>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">What You'll Get</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Security Posture Assessment</h4>
                    <p className="text-sm text-gray-600">Continuous monitoring of misconfigurations</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Compliance Reports</h4>
                    <p className="text-sm text-gray-600">CIS, NIST, SOC2, PCI-DSS, HIPAA</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Risk Prioritization</h4>
                    <p className="text-sm text-gray-600">Focus on what matters most</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Key className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">IAM Analysis</h4>
                    <p className="text-sm text-gray-600">Identity and access management review</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                <Plus className="w-5 h-5" />
                Connect Your Cloud Account
              </button>
              <button
                onClick={toggleDemoMode}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                <ToggleRight className="w-5 h-5" />
                Try Demo Mode
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {accounts.map((account) => (
            <div key={account.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Provider Header */}
              <div className={`p-4 ${
                account.provider === 'aws' ? 'bg-orange-500' :
                account.provider === 'azure' ? 'bg-blue-500' : 'bg-green-500'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-3xl">{providerIcons[account.provider]}</span>
                  <span className="text-white text-sm uppercase font-medium">{account.provider}</span>
                </div>
              </div>
              
              {/* Account Details */}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{account.name}</h3>
                
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  {account.provider === 'aws' && account.credentials?.account_id && (
                    <p className="flex items-center gap-2">
                      <Server className="w-4 h-4" />
                      Account: {account.credentials.account_id}
                    </p>
                  )}
                  {account.provider === 'azure' && account.credentials?.subscription_id && (
                    <p className="flex items-center gap-2">
                      <Server className="w-4 h-4" />
                      Subscription: {account.credentials.subscription_id}
                    </p>
                  )}
                  {account.provider === 'gcp' && account.credentials?.project_id && (
                    <p className="flex items-center gap-2">
                      <Server className="w-4 h-4" />
                      Project: {account.credentials.project_id}
                    </p>
                  )}
                  <p className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {account.regions?.length || 0} regions
                  </p>
                </div>

                {/* AWS Preflight Badge — only for real AWS accounts with a role_arn */}
                {account.provider === 'aws' && account.credentials?.role_arn && !account.is_demo && (
                  <PreflightBadge
                    account={account}
                    defaultOpen={openWizardAccountId === account.id}
                  />
                )}

                {/* Per-account auto-scan cadence (default 12h) */}
                <ScheduleSelector account={account} />

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{account.total_findings || 0}</p>
                    <p className="text-xs text-gray-500">Findings</p>
                  </div>
                  <div className={`rounded-lg p-3 text-center ${
                    account.critical_findings > 0 ? 'bg-red-50' : 'bg-green-50'
                  }`}>
                    <p className={`text-2xl font-bold ${
                      account.critical_findings > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {account.critical_findings || 0}
                    </p>
                    <p className="text-xs text-gray-500">Critical</p>
                  </div>
                </div>

                {/* Status & Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className={`flex items-center gap-1 text-sm ${
                    account.status === 'active' ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {account.status === 'active' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <AlertTriangle className="w-4 h-4" />
                    )}
                    {account.status}
                  </span>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`${basePath}/scan?account=${account.id}`)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                      title="Scan Account"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteAccount(account.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete Account"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {account.last_scan_at && (
                  <p className="text-xs text-gray-400 mt-2">
                    Last scan: {new Date(account.last_scan_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Account Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Add Cloud Account</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cloud Provider
                </label>
                <div className="grid grid-cols-4 gap-3">
                  {['aws', 'azure', 'gcp', 'onprem'].map((provider) => (
                    <button
                      key={provider}
                      onClick={() => setNewAccount(prev => ({ ...prev, provider, regions: [] }))}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        newAccount.provider === provider
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-3xl">{providerIcons[provider] || '🏢'}</span>
                      <p className="text-sm font-medium mt-2 uppercase">{provider === 'onprem' ? 'On-Prem' : provider}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Account Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Name
                </label>
                <input
                  type="text"
                  value={newAccount.name}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={`My ${newAccount.provider === 'onprem' ? 'On-Premises' : newAccount.provider.toUpperCase()} Environment`}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Provider-specific Configuration Guide */}
              {newAccount.provider === 'aws' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-orange-800 flex items-center gap-2">
                    <span className="text-xl">☁️</span> AWS Configuration Steps
                  </h4>
                  <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 mb-2">
                    <p className="text-xs font-bold text-orange-900">Beskar AWS Account ID</p>
                    <p className="text-lg font-mono font-bold text-orange-800" data-testid="beskar-aws-account-id">9925-5878-5763</p>
                    <p className="text-[11px] text-orange-600">Use this Account ID when creating cross-account IAM roles</p>
                  </div>
                  <ol className="text-sm text-orange-700 space-y-2 list-decimal list-inside">
                    <li><strong>Create IAM Role:</strong> Go to IAM &gt; Roles &gt; Create Role &gt; AWS Account &gt; Another AWS account &gt; Enter <code className="bg-orange-100 px-1 rounded font-bold">992558785763</code></li>
                    <li><strong>Set External ID:</strong> Check "Require external ID" &gt; Enter your tenant identifier for added security</li>
                    <li><strong>Attach <code className="bg-orange-100 px-1 rounded">SecurityAudit</code> Managed Policy</strong></li>
                    <li><strong>Create Inline Policy</strong> for additional read access (see below)</li>
                    <li><strong>Enable CloudTrail:</strong> Create a trail with management events in the target regions</li>
                    <li><strong>Enable AWS Config:</strong> Record all resource types for continuous compliance monitoring</li>
                    <li><strong>Copy Role ARN</strong> and paste below</li>
                  </ol>
                  <details className="mt-2">
                    <summary className="text-xs font-semibold text-orange-800 cursor-pointer hover:underline">View Trust Policy JSON</summary>
                    <pre className="mt-1 p-2 bg-orange-100 rounded text-[11px] font-mono overflow-x-auto whitespace-pre">
{`{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Principal": {"AWS": "arn:aws:iam::992558785763:root"},
    "Action": "sts:AssumeRole",
    "Condition": {
      "StringEquals": {
        "sts:ExternalId": "<YOUR_TENANT_EXTERNAL_ID>"
      }
    }
  }]
}`}
                    </pre>
                  </details>
                  <details className="mt-1">
                    <summary className="text-xs font-semibold text-orange-800 cursor-pointer hover:underline">View Required Inline Policy</summary>
                    <pre className="mt-1 p-2 bg-orange-100 rounded text-[11px] font-mono overflow-x-auto whitespace-pre">
{`{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": [
      "cloudtrail:DescribeTrails",
      "cloudtrail:GetTrailStatus",
      "cloudtrail:LookupEvents",
      "config:DescribeConfigRules",
      "config:DescribeComplianceByConfigRule",
      "config:GetComplianceDetailsByConfigRule",
      "guardduty:ListDetectors",
      "guardduty:GetFindings",
      "access-analyzer:ListAnalyzers",
      "inspector2:ListFindings",
      "securityhub:GetFindings"
    ],
    "Resource": "*"
  }]
}`}
                    </pre>
                  </details>
                  <div className="mt-2 p-2 bg-orange-100 rounded text-xs font-mono">
                    <p className="font-bold mb-1">CLI Quick Setup:</p>
                    <code className="text-[11px]">aws iam create-role --role-name BeskarSecurityAuditRole --assume-role-policy-document file://trust-policy.json</code><br/>
                    <code className="text-[11px]">aws iam attach-role-policy --role-name BeskarSecurityAuditRole --policy-arn arn:aws:iam::aws:policy/SecurityAudit</code>
                  </div>
                  <div className="mt-2 space-y-2">
                    <input
                      type="text"
                      placeholder="Role ARN: arn:aws:iam::123456789012:role/BeskarSecurityAuditRole"
                      value={newAccount.credentials.role_arn || ''}
                      onChange={(e) => setNewAccount(prev => ({...prev, credentials: {...prev.credentials, role_arn: e.target.value}}))}
                      className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      data-testid="aws-role-arn-input"
                    />
                    <input
                      type="text"
                      placeholder="External ID (optional)"
                      value={newAccount.credentials.external_id || ''}
                      onChange={(e) => setNewAccount(prev => ({...prev, credentials: {...prev.credentials, external_id: e.target.value}}))}
                      className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      data-testid="aws-external-id-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleTestConnection}
                      disabled={testingConnection}
                      className="flex items-center gap-2 px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 disabled:opacity-50"
                      data-testid="test-aws-connection-btn"
                    >
                      {testingConnection ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      Test Connection
                    </button>
                    {connectionStatus && (
                      <span className={`text-sm font-medium ${connectionStatus === 'success' ? 'text-green-600' : 'text-red-600'}`} data-testid="connection-status">
                        {connectionStatus === 'success' ? 'Connected successfully' : connectionStatus === 'error' ? 'Connection failed — check credentials' : connectionStatus}
                      </span>
                    )}
                  </div>
                  <a href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_create_for-user.html" target="_blank" rel="noopener noreferrer" className="text-orange-600 text-sm hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> AWS IAM Cross-Account Roles Documentation
                  </a>
                </div>
              )}

              {newAccount.provider === 'azure' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-blue-800 flex items-center gap-2">
                    <span className="text-xl">☁️</span> Azure Configuration Steps
                  </h4>
                  <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
                    <li><strong>Register App:</strong> Azure AD &gt; App registrations &gt; New registration &gt; Name: "Beskar Security Scanner"</li>
                    <li><strong>Create Client Secret:</strong> Certificates & secrets &gt; New client secret &gt; Copy value immediately</li>
                    <li><strong>Assign Reader Role:</strong> Subscriptions &gt; IAM &gt; Add Role: <code className="bg-blue-100 px-1 rounded">Reader</code> + <code className="bg-blue-100 px-1 rounded">Security Reader</code></li>
                    <li><strong>Enable Defender for Cloud:</strong> Microsoft Defender &gt; Environment settings &gt; Enable CSPM</li>
                    <li><strong>Enable Resource Graph:</strong> Required for cross-subscription queries</li>
                    <li><strong>Collect IDs:</strong> Tenant ID, Application (Client) ID, Client Secret, Subscription ID</li>
                  </ol>
                  <details className="mt-2">
                    <summary className="text-xs font-semibold text-blue-800 cursor-pointer hover:underline">View Azure CLI Setup Commands</summary>
                    <pre className="mt-1 p-2 bg-blue-100 rounded text-[11px] font-mono overflow-x-auto whitespace-pre">
{`# Register App
az ad app create --display-name "Beskar Security Scanner"

# Create Service Principal
az ad sp create --id <APP_ID>

# Create Client Secret
az ad app credential reset --id <APP_ID> --years 2

# Assign Reader role to subscription
az role assignment create \\
  --assignee <APP_ID> \\
  --role "Reader" \\
  --scope /subscriptions/<SUBSCRIPTION_ID>

# Assign Security Reader role
az role assignment create \\
  --assignee <APP_ID> \\
  --role "Security Reader" \\
  --scope /subscriptions/<SUBSCRIPTION_ID>`}
                    </pre>
                  </details>
                  <div className="mt-2 p-2 bg-blue-100 rounded text-xs">
                    <p className="font-bold mb-1">Required Azure AD API Permissions:</p>
                    <p className="font-mono text-[11px]">Directory.Read.All, Policy.Read.All, SecurityEvents.Read.All (Application type)</p>
                  </div>
                  <div className="mt-2 space-y-2">
                    <input
                      type="text"
                      placeholder="Tenant ID (Directory ID)"
                      value={newAccount.credentials.tenant_id || ''}
                      onChange={(e) => setNewAccount(prev => ({...prev, credentials: {...prev.credentials, tenant_id: e.target.value}}))}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      data-testid="azure-tenant-id-input"
                    />
                    <input
                      type="text"
                      placeholder="Application (Client) ID"
                      value={newAccount.credentials.client_id || ''}
                      onChange={(e) => setNewAccount(prev => ({...prev, credentials: {...prev.credentials, client_id: e.target.value}}))}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      data-testid="azure-client-id-input"
                    />
                    <input
                      type="password"
                      placeholder="Client Secret"
                      value={newAccount.credentials.client_secret || ''}
                      onChange={(e) => setNewAccount(prev => ({...prev, credentials: {...prev.credentials, client_secret: e.target.value}}))}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      data-testid="azure-client-secret-input"
                    />
                    <input
                      type="text"
                      placeholder="Subscription ID"
                      value={newAccount.credentials.subscription_id || ''}
                      onChange={(e) => setNewAccount(prev => ({...prev, credentials: {...prev.credentials, subscription_id: e.target.value}}))}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      data-testid="azure-subscription-id-input"
                    />
                  </div>
                  <a href="https://learn.microsoft.com/en-us/entra/identity-platform/quickstart-register-app" target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Azure App Registration Documentation
                  </a>
                </div>
              )}

              {newAccount.provider === 'gcp' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-green-800 flex items-center gap-2">
                    <span className="text-xl">☁️</span> GCP Configuration Steps
                  </h4>
                  <ol className="text-sm text-green-700 space-y-2 list-decimal list-inside">
                    <li><strong>Create Service Account:</strong> IAM & Admin &gt; Service Accounts &gt; Create &gt; Name: "beskar-security-scanner"</li>
                    <li><strong>Grant Roles:</strong> <code className="bg-green-100 px-1 rounded">Security Reviewer</code>, <code className="bg-green-100 px-1 rounded">Viewer</code>, and <code className="bg-green-100 px-1 rounded">Security Center Findings Viewer</code></li>
                    <li><strong>Generate JSON Key:</strong> Service account &gt; Keys &gt; Add Key &gt; JSON &gt; Download</li>
                    <li><strong>Enable Required APIs</strong> (see below)</li>
                    <li><strong>Enable Security Command Center</strong> for advanced findings</li>
                    <li><strong>Upload JSON key</strong> or paste contents below</li>
                  </ol>
                  <details className="mt-2">
                    <summary className="text-xs font-semibold text-green-800 cursor-pointer hover:underline">View gcloud CLI Setup Commands</summary>
                    <pre className="mt-1 p-2 bg-green-100 rounded text-[11px] font-mono overflow-x-auto whitespace-pre">
{`# Create service account
gcloud iam service-accounts create beskar-security-scanner \\
  --display-name="Beskar Security Scanner"

# Grant roles
gcloud projects add-iam-policy-binding PROJECT_ID \\
  --member="serviceAccount:beskar-security-scanner@PROJECT_ID.iam.gserviceaccount.com" \\
  --role="roles/iam.securityReviewer"

gcloud projects add-iam-policy-binding PROJECT_ID \\
  --member="serviceAccount:beskar-security-scanner@PROJECT_ID.iam.gserviceaccount.com" \\
  --role="roles/viewer"

# Enable required APIs
gcloud services enable \\
  cloudasset.googleapis.com \\
  securitycenter.googleapis.com \\
  cloudresourcemanager.googleapis.com \\
  compute.googleapis.com \\
  container.googleapis.com \\
  sqladmin.googleapis.com \\
  storage-api.googleapis.com

# Generate JSON key
gcloud iam service-accounts keys create beskar-key.json \\
  --iam-account=beskar-security-scanner@PROJECT_ID.iam.gserviceaccount.com`}
                    </pre>
                  </details>
                  <div className="mt-2 p-2 bg-green-100 rounded text-xs">
                    <p className="font-bold mb-1">Required APIs:</p>
                    <p className="font-mono text-[11px]">Cloud Asset API, Security Command Center API, Cloud Resource Manager API, Compute Engine API, Cloud SQL Admin API</p>
                  </div>
                  <div className="mt-2 space-y-2">
                    <input
                      type="text"
                      placeholder="GCP Project ID"
                      value={newAccount.credentials.project_id || ''}
                      onChange={(e) => setNewAccount(prev => ({...prev, credentials: {...prev.credentials, project_id: e.target.value}}))}
                      className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                      data-testid="gcp-project-id-input"
                    />
                    <textarea
                      placeholder='Paste Service Account JSON key contents here...'
                      value={newAccount.credentials.service_account_key || ''}
                      onChange={(e) => setNewAccount(prev => ({...prev, credentials: {...prev.credentials, service_account_key: e.target.value}}))}
                      className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 font-mono"
                      rows={4}
                      data-testid="gcp-service-key-input"
                    />
                  </div>
                  <a href="https://cloud.google.com/iam/docs/creating-managing-service-accounts" target="_blank" rel="noopener noreferrer" className="text-green-600 text-sm hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> GCP Service Account Documentation
                  </a>
                </div>
              )}

              {newAccount.provider === 'onprem' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-purple-800 flex items-center gap-2">
                    <span className="text-xl">🏢</span> On-Premises Configuration Steps
                  </h4>
                  <ol className="text-sm text-purple-700 space-y-2 list-decimal list-inside">
                    <li>Deploy the Beskar Agent on your infrastructure</li>
                    <li>Configure network access to your servers (SSH/WinRM)</li>
                    <li>Create a service account with read-only access</li>
                    <li>Register the agent with your API key</li>
                  </ol>
                  <div className="mt-3 p-2 bg-purple-100 rounded">
                    <p className="text-xs text-purple-800">
                      <strong>Agent Download:</strong> <code>curl -sSL https://beskar.io/agent/install.sh | bash</code>
                    </p>
                  </div>
                </div>
              )}

              {/* Region Selection (not for on-prem) */}
              {newAccount.provider !== 'onprem' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Regions to Scan
                  </label>
                  <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto p-1">
                    {(providerRegions[newAccount.provider] || []).map((region) => (
                      <button
                        key={region}
                        onClick={() => toggleRegion(region)}
                        className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                          newAccount.regions.includes(region)
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        {region}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {newAccount.regions.length === 0 
                      ? 'Default regions will be selected if none chosen'
                      : `${newAccount.regions.length} region(s) selected`}
                  </p>
                </div>
              )}

              {/* Security Notice */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-800">
                  <strong>Security Note:</strong> Credentials are encrypted at rest and transmitted over TLS. 
                  We recommend using dedicated read-only service accounts with minimum required permissions. 
                  {/* Demo Mode banner removed — demo state is now backend-driven via DemoModeToggle */}
                </p>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAccount}
                disabled={!newAccount.name}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmidalaAccounts;
